import {
  EarnConfigType,
  EarnBurnRewardsPayloadType,
  EarnRewardsConditionPayloadType,
  CreditNameUpdatePayloadType,
  CreditNameType,
  BurnConfigType,
  BurnConditionsConfigType,
  BurnRewardsConditionPayloadType,
  BlockingConditions,
  RewardsConfigType
} from 'src/lib/types/rewards'

export const rewardKPIMetadata = [
  { key: 'total_credit', displayText: 'Total credit issued' },
  { key: 'total_credits_used', displayText: 'Total credit used' },
  { key: 'expiring_this_month', displayText: 'Expiring this month' }
]

export const customerPerformanceData = [
  { key: 'total_burn', displayText: 'Credit used' },
  { key: 'total_earn', displayText: 'Credit issued' },
  { key: 'total_wallet_balance', displayText: 'Wallet balance' }
]

export const orderDetailsMetadata = [
  { key: 'orderType', displayText: 'Order type' },
  { key: 'platform', displayText: 'Platform' },
  { key: 'orderId', displayText: 'Order Id' },
  { key: 'checkoutId', displayText: 'Checkout Id' },
  { key: 'orderAmount', displayText: 'Order amount' },
  { key: 'orderCreatedAt', displayText: 'Created at' }
]

export const getEarnRewardsPayload = (earnConfig: EarnConfigType): EarnBurnRewardsPayloadType => {
  const payload: EarnBurnRewardsPayloadType = {
    event_reward_rule_map: {
      ORDER_PLACED: {
        earn_burn_type: 'RATIO',
        action: 'EARN',
        amount: earnConfig.earnRatio ? earnConfig.earnRatio / 100 : 0,
        expiry_time: earnConfig.expiry ? earnConfig.expiry * 86400000 : null
      }
    },
    currency: 'INR',

    order_type_confirmation_strategies: {
      ONLINE: {
        type: 'DELAYED',
        duration_after: earnConfig.delay ? earnConfig.delay * 86400000 : 0
      },
      COD: {
        type: 'DELAYED',
        duration_after: earnConfig.delay ? earnConfig.delay * 86400000 : 0
      }
    }
  }
  return payload
}

export const generateEarnRuleDialogFooterText = (earnConfig: EarnConfigType): string => {
  let finalTxt = ''
  if (!earnConfig?.earnRatio) {
    return 'Customers will not earn rewards on orders.'
  }
  finalTxt = `Customers will earn ₹${earnConfig.earnRatio * 10} on a ₹1000 order`
  if (earnConfig?.delay) {
    finalTxt = `${finalTxt} ${earnConfig.delay} ${earnConfig.delay > 1 ? 'days' : 'day'} later`
  }
  if (earnConfig?.expiry) {
    finalTxt = `${finalTxt}. These earnings will expire in ${earnConfig.expiry}  ${
      earnConfig.expiry > 1 ? 'days' : 'day'
    }`
  }

  return `${finalTxt}.`
}

export const getEarnRewardsMinOVPayload = (minOV: number): EarnRewardsConditionPayloadType => {
  const payload: EarnRewardsConditionPayloadType = {
    event_reward_rule_map: {
      ORDER_PLACED: {
        blocking_conditions: [
          {
            type: 'MIN_CART_VALUE',
            value: minOV ?? 0
          }
        ]
      }
    }
  }
  return payload
}

export const generateMinOVDialogFooterText = (minOV: number, txnType: 'EARN' | 'BURN'): string => {
  if (!Boolean(minOV))
    return txnType === 'EARN'
      ? 'Customers will earn rewards on all orders'
      : 'Customers can redeem rewards on all orders'
  return txnType === 'EARN'
    ? `Customers will earn rewards only on orders valued ₹${minOV} and above`
    : `Customers can redeem rewards only on orders valued ₹${minOV} and above`
}

export const getCreditNameUpdatePayload = (creditName: CreditNameType): CreditNameUpdatePayloadType => {
  const payload: CreditNameUpdatePayloadType = {
    coin_name: creditName.singularName,
    coin_name_plural: creditName.pluralName
  }
  return payload
}

export const getBurnRewardsPayload = (burnConfig: BurnConfigType) => {
  const payload: EarnBurnRewardsPayloadType = {
    event_reward_rule_map: {
      REDEMPTION: {
        earn_burn_type: 'RATIO',
        action: 'BURN',
        amount: burnConfig.burnRatio ? burnConfig.burnRatio / 100 : 0
      }
    },
    currency: 'INR'
  }
  return payload
}

export const getBurnRewardsConditionPayload = (
  burnConditionsConfig: BurnConditionsConfigType,
  conditionType: keyof BurnConditionsConfigType
): BurnRewardsConditionPayloadType => {
  let blockingCondition: BlockingConditions

  switch (conditionType) {
    case 'min_ov': {
      blockingCondition = {
        type: 'MIN_CART_VALUE',
        value: burnConditionsConfig.min_ov
      }
      break
    }
  }
  const payload: BurnRewardsConditionPayloadType = {
    event_reward_rule_map: {
      REDEMPTION: {
        blocking_conditions: [{ ...blockingCondition }]
      }
    }
  }
  return payload
}

export const generateBurnRuleDialogFooterText = (burnConfig: BurnConfigType): string => {
  const burnratio = burnConfig.burnRatio ?? 0
  if (!burnratio) {
    return `Customers cannot redeem rewards on orders`
  }
  if (burnratio === 100) {
    return `Customers can pay for their entire order using their rewards wallet`
  }

  return `Customers can redeem up to ₹${burnratio * 10} for an order valued at ₹1,000 using their rewards wallet`
}

export const generateEarnRewardsPreviewText = (earnConfig: EarnConfigType): string => {
  if (earnConfig.earnRatio === 0) {
    return `0%`
  }
  let result = `${earnConfig.earnRatio}%`
  if (earnConfig.delay) {
    result += ` · Credit in ${earnConfig.delay} ${earnConfig.delay > 1 ? 'days' : 'day'}`
  }
  if (earnConfig.expiry) {
    result += ` · Valid for ${earnConfig.expiry} ${earnConfig.expiry > 1 ? 'days' : 'day'}`
  }
  return result
}

export const parseRewardsConfigFromBackend = (rewardsConfig: any) => {
  const parsedRewardsConfig: RewardsConfigType = {
    creditName: {
      singularName: rewardsConfig?.coin_name,
      pluralName: rewardsConfig?.coin_name_plural
    },
    earnMessage: {
      enable: rewardsConfig?.personalized_wallet_display_message_enabled,
      line_1: rewardsConfig?.rewards_earned_message,
      line_2: rewardsConfig?.currency_earned_message
    },
    earnTileCustomiseUi: {
      textColor: rewardsConfig?.earn_tile_customise_ui?.text_color,
      outlineColor: rewardsConfig?.earn_tile_customise_ui?.outline_color,
      backgroundColor: rewardsConfig?.earn_tile_customise_ui?.background_color,
      isCreditNameVisible: rewardsConfig?.earn_tile_customise_ui?.is_credit_name_visible,
      outline: rewardsConfig?.earn_tile_customise_ui?.outline
    }
  }
  return parsedRewardsConfig
}
